import React from "react";
import MetricCard from "./MetricCard";
import { formatUSD } from "../../utils/formatters";
import type { SelfSetMetrics } from "../../utils/selfSetMetrics";

// =============================================================================
// SELF-SET PANEL — the closer-sourced slice of the admin dashboard
// =============================================================================
// Rendered in place of <SalesPerformanceCard> when the header's Self-Set toggle is on.
// Same tile vocabulary as that card so the eye doesn't have to re-learn the layout;
// the NUMBERS are the difference, not the shape.
//
// WHY THERE IS NO SANKEY FUNNEL IN SELF-SET MODE (deliberate, not a gap):
//   The funnel's upstream stages — Leads In, Booked — are source-attributed
//   (`lead_funnel_source`: webinar / typeform / dm) and count the setter-sourced path
//   into a call. A self-set deal has no such path by definition: the closer sourced the
//   lead directly, and no pre-call backs it. Rendering those stages for a self-set slice
//   would print structural zeros that read as "we booked nothing" rather than "this
//   question does not apply." So the panel starts at the first stage that IS knowable —
//   the call itself — and says so on its face.
// =============================================================================

interface SelfSetPanelProps {
  metrics: SelfSetMetrics;
  /** Range label echoed from the dashboard header, e.g. "Last 30 Days". */
  rangeLabel?: string | null;
}

const SelfSetPanel: React.FC<SelfSetPanelProps> = ({ metrics, rangeLabel }) => {
  const empty = metrics.outcomeRows === 0;

  return (
    <div className="liquid-glass anim-lift p-4 space-y-5 h-full" data-testid="self-set-panel">
      <div>
        <h3 className="token-14s mb-3 flex items-center gap-2">
          <span className="section-bar" />
          Self-Set Performance
          <span className="token-10 text-glass-muted font-normal normal-case tracking-normal ml-1">
            closer-sourced · no setter{rangeLabel ? ` · ${rangeLabel}` : ""}
          </span>
        </h3>

        {empty ? (
          // Explicit empty state. NEVER render zero tiles here — a grid of 0s and 0.0%
          // is indistinguishable from a broken data path, which is the exact failure
          // this page already shipped once (`0 setter · 0 dialer · 0 self`).
          <div
            className="rounded-2xl border border-black/[0.06] bg-black/[0.02] px-4 py-6 text-center"
            data-testid="self-set-empty"
          >
            <div className="token-13m text-glass">No self-set calls in this range</div>
            <div className="token-11 text-glass-muted mt-1">
              Every sales call in range had a setter or dialer attributed to it.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <MetricCard label="Self-Set Cash" value={formatUSD(metrics.cash)} hero />
            <MetricCard label="Deals Closed" value={metrics.closed.toString()} />
            <MetricCard label="Close Rate" value={`${metrics.closeRate.toFixed(1)}%`} />
            <MetricCard
              label="Show-up Rate"
              value={`${metrics.showUpRate.toFixed(1)}%`}
              sub={`${metrics.noShows} no-show${metrics.noShows === 1 ? "" : "s"}`}
            />
          </div>
        )}
      </div>

      {!empty && (
        <>
          <div className="border-t border-black/[0.06]" />

          <div>
            <h3 className="token-14s mb-3 flex items-center gap-2">
              <span className="section-bar" />
              Call Pipeline
              <span className="token-10 text-glass-muted font-normal normal-case tracking-normal ml-1">
                self-set only →
              </span>
            </h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
              <div>
                <div className="token-10u text-glass-muted">Calls Taken</div>
                <div className="token-15 text-glass">{metrics.callsTaken.toLocaleString()}</div>
              </div>
              <div>
                <div className="token-10u text-glass-muted">Shows</div>
                <div className="token-15 text-glass">{metrics.shows.toLocaleString()}</div>
              </div>
              <div>
                <div className="token-10u text-glass-muted">No-Shows</div>
                <div className="token-15 text-glass">{metrics.noShows.toLocaleString()}</div>
              </div>
              <div>
                <div className="token-10u text-glass-muted">Avg Cash / Close</div>
                <div className="token-15 text-glass">{formatUSD(metrics.avgCashPerClose)}</div>
              </div>
            </div>
          </div>

          <div className="border-t border-black/[0.06]" />

          <div>
            <h3 className="token-14s mb-3 flex items-center gap-2">
              <span className="section-bar" />
              Cash Yield
            </h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <div className="token-10u text-glass-muted">Revenue Closed</div>
                <div className="token-15 text-glass">{formatUSD(metrics.revenue)}</div>
              </div>
              <div>
                <div className="token-10u text-glass-muted">Cash / Call Taken</div>
                <div className="token-15 text-glass">
                  {formatUSD(metrics.callsTaken > 0 ? metrics.cash / metrics.callsTaken : 0)}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Receipt. States the denominator and the predicate on the face of the panel so
          nobody has to guess what "self-set" counted — CLAUDE.md counts-before-verdict. */}
      <div className="token-9 text-glass-muted pt-1">
        n={metrics.outcomeRows} self-set outcome row{metrics.outcomeRows === 1 ? "" : "s"} · closer
        attributed, no setter on the post-call note
      </div>
    </div>
  );
};

export default SelfSetPanel;
